import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { PhoneIcon, MapPinIcon, ShareIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { formatPhoneNumber } from "../../globalFunctions/PhoneNumberFormatter";

export default function CompanyPage() {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/company/by-slug/${slug}`)
      .then((res) => res.json())
      .then(setCompany)
      .catch(console.error);
  }, [slug]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedSlug(company.slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  if (!company) return <div>Chargement...</div>;

  const googleLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    company.address
  )}`;

  return (
    <div className="bg-white h-screen">
      <div className="max-w-5xl mx-auto px-4 py-8 ">
        {/* Bloc principal : image à gauche, texte à droite */}
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Logo/image */}
          <div className="w-full md:w-1/2 flex justify-center">
            <div className="w-full max-w-md h-[300px] md:h-[400px] bg-white rounded-lg shadow flex items-center justify-center overflow-hidden">
              <img
                src={company.imageUrl}
                alt={company.name}
                className="max-w-full max-h-full object-contain p-4"
              />
            </div>
          </div>

          {/* Infos */}
          <div className="flex-1  space-y-4 ">
            <div className="flex flex-col ">
              <h1 className="text-3xl font-bold text-gray-800">
                {company.name}
              </h1>

              {company && (
                <button
                  className="transition-transform rounded-full group"
                  onClick={handleCopy}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {company && copiedSlug === company.slug ? (
                      <motion.span
                        key="check"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="flex items-center gap-1 text-green-600 text-sm"
                      >
                        {t("global.copiedToClipboard")}
                        <CheckIcon className="w-5 h-5" />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="clipboard"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="flex items-center gap-1 text-gray-500 italic text-sm group-hover:text-primary"
                      >
                        Partager{" "}
                        <ShareIcon className="w-4 h-4 group-hover:text-primary" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              )}
            </div>

            <div className="mt-4 bg-white/80 border rounded-lg p-4 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <MapPinIcon className="w-5 h-5 text-primary" />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    company.address
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {company.address}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <PhoneIcon className="w-5 h-5 text-primary" />
                <span>{formatPhoneNumber(company.phoneNumber)}</span>
              </div>
            </div>

            {/* Liens externes */}
            <div className="space-y-2 text-sm">
              {company.website && (
                <div>
                  <span className="font-semibold">Site :</span>{" "}
                  <a
                    href={company.website}
                    target="_blank"
                    className="text-primary hover:underline"
                  >
                    {company.website}
                  </a>
                </div>
              )}
              {company.twitchUrl && (
                <div>
                  <span className="font-semibold">Twitch :</span>{" "}
                  <a
                    href={company.twitchUrl}
                    target="_blank"
                    className="text-purple-600 hover:underline"
                  >
                    {company.twitchUrl}
                  </a>
                </div>
              )}
              {company.spotifyUrl && (
                <div>
                  <span className="font-semibold">Spotify :</span>{" "}
                  <a
                    href={company.spotifyUrl}
                    target="_blank"
                    className="text-green-600 hover:underline"
                  >
                    {company.spotifyUrl}
                  </a>
                </div>
              )}
              {company.youtubeUrl && (
                <div>
                  <span className="font-semibold">YouTube :</span>{" "}
                  <a
                    href={company.youtubeUrl}
                    target="_blank"
                    className="text-red-600 hover:underline"
                  >
                    {company.youtubeUrl}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section Produits (plus tard) */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-4"></h2>
          {/* Liste de produits ici */}
        </div>
      </div>
    </div>
  );
}
